/**
 * The single source of truth for public-route metadata.
 *
 * Three consumers read this map and nothing else:
 *   - `scripts/prerender.mjs`        — injects head tags into prerendered HTML
 *   - `scripts/generate-seo-files.mjs` — emits robots.txt + sitemap.xml
 *   - `src/components/Seo.tsx`       — sets the title on client-side navigation
 *
 * Adding a route here automatically prerenders it, sitemaps it and titles it.
 * There is deliberately no second list to keep in step — that is how
 * `contracts/API.md` fell 27 routes behind the infra it describes.
 *
 * Pure data: no React, no I/O, no `import.meta.env`. Node build scripts and
 * the browser bundle both import it, so it must run in either.
 */

export interface RouteSeo {
  /** Router path, exactly as declared in `App.tsx`. */
  path: string
  /** Full `<title>`. Unique per page. */
  title: string
  /** `<meta name="description">`. 70–160 chars — see routes.test.ts. */
  description: string
  /** Absolute path under `/og/`, resolved against CANONICAL_ORIGIN at build. */
  ogImage: string
  changefreq: 'weekly' | 'monthly'
  /** Sitemap hint, 0.0–1.0. Relative within our own site only. */
  priority: number
}

/** Production origin. Sitemap and canonical URLs are always absolute against this. */
export const CANONICAL_ORIGIN = 'https://e404.app'

/**
 * Path prefixes crawlers must not follow. Authenticated surfaces, one-shot
 * tokens, and auth callbacks — none of which render anything useful to a
 * crawler, and some of which leak structure.
 *
 * `/stadium/` is disallowed but `/stadium/demo` is a public route: the demo is
 * a marketing surface, real event stadiums are not. robots.txt Allow beats
 * Disallow on a longer match, which Task 3 relies on.
 */
export const DISALLOWED_PREFIXES: readonly string[] = [
  '/app/',
  '/onboarding',
  '/auth/',
  '/i/',
  '/e/',
  '/stadium/',
  '/verify-email',
  '/forgot-password',
  '/login',
  '/signup',
  '/nda-required',
]

export const PUBLIC_ROUTES: readonly RouteSeo[] = [
  {
    path: '/',
    title: 'e404 — the home of your gaming clan',
    description:
      'Run your gaming clan in one place: roster, events, tournaments and who is online right now. Discord still gets the ping. Free up to 10 members.',
    ogImage: '/og/home.png',
    changefreq: 'weekly',
    priority: 1.0,
  },
  {
    path: '/explore/squadrons',
    title: 'Squadrons — one roster across Discord, Steam and Twitch | e404',
    description:
      'Your members are a Discord tag, a Steam ID and a Twitch channel. Link them once and your roster shows people, not three unconnected handles.',
    ogImage: '/og/squadrons.png',
    changefreq: 'monthly',
    priority: 0.8,
  },
  {
    path: '/explore/tournaments',
    title: 'Tournaments — seeding, scheduling and prize pots | e404',
    description:
      'Run the whole tournament without a second tool: seeding, match scheduling, captain result reporting, Elo, and prize pots paid straight to the winners.',
    ogImage: '/og/tournaments.png',
    changefreq: 'monthly',
    priority: 0.8,
  },
  {
    path: '/explore/live',
    title: 'Live — every squadron stream in one place | e404',
    description:
      'Every Twitch stream your squadron is running, on one page — plus play windows and a drop-in queue so people can find a game tonight.',
    ogImage: '/og/live.png',
    changefreq: 'monthly',
    priority: 0.8,
  },
  {
    path: '/pricing',
    title: 'Pricing — free up to 10 members | e404',
    description:
      'Free for squadrons up to 10 members, no card required. Paid tiers scale to 25, 50 and 300. See what each tier includes and how member fees work.',
    ogImage: '/og/pricing.png',
    changefreq: 'monthly',
    priority: 0.9,
  },
  {
    path: '/stadium/demo',
    title: 'Stadium — put your tournament on the big screen | e404',
    description:
      'A live tournament view built for a TV or a second monitor: current matches, upcoming rounds and results updating as captains report them.',
    ogImage: '/og/stadium.png',
    changefreq: 'monthly',
    priority: 0.6,
  },
]

/** Look up metadata for a router path. Returns undefined for non-public paths. */
export function seoForPath(path: string): RouteSeo | undefined {
  return PUBLIC_ROUTES.find((r) => r.path === path)
}
